import { useState, useEffect, KeyboardEvent } from "react";
import { Select } from "@/components/tailwind/select";
import { Input, InputGroup } from "@/components/tailwind/input";
import { Checkbox } from "@/components/tailwind/checkbox";
import { Field, Label } from "@/components/tailwind/fieldset";
import { Button } from "@/components/tailwind/button";
import { LedgerEntryType } from "@/models/ledger";
import { MagnifyingGlassIcon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/16/solid";
import { useToast } from "@/hooks/use-toast";
import { useLedgerTranslation } from "@/i18n/hooks";

interface LedgerFiltersProps {
    year: number;
    month: number;
    type: LedgerEntryType | 'ALL';
    search: string;
    searchAll: boolean;
    earliestYear: number | null;
    onYearChange: (year: number) => void;
    onMonthChange: (month: number) => void;
    onTypeChange: (type: LedgerEntryType | 'ALL') => void;
    onSearchChange: (search: string) => void;
    onSearchAllChange: (searchAll: boolean) => void;
}

export function LedgerFilters({
    year,
    month,
    type,
    search,
    searchAll,
    earliestYear,
    onYearChange,
    onMonthChange,
    onTypeChange,
    onSearchChange,
    onSearchAllChange
}: LedgerFiltersProps) {
    const { toast } = useToast();
    const { t } = useLedgerTranslation();
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    
    // State for direct input
    const [yearInput, setYearInput] = useState(year.toString());
    const [monthInput, setMonthInput] = useState(month.toString().padStart(2, '0'));
    
    const minYear = earliestYear || currentYear;
    const maxYear = currentYear;
    
    // Sync input fields when props change
    useEffect(() => {
        setYearInput(year.toString());
        setMonthInput(month.toString().padStart(2, '0'));
    }, [year, month]);
    
    // Check if navigation is possible
    const canGoToPreviousMonth = () => {
        return year > minYear || (year === minYear && month > 1);
    };
    
    const canGoToNextMonth = () => {
        return year < maxYear || (year === maxYear && month < currentMonth);
    };
    
    // Navigate months with year overflow
    const handlePreviousMonth = () => {
        if (!canGoToPreviousMonth()) return;
        
        if (month === 1) {
            // Go to December of previous year
            const prevYear = year - 1;
            onYearChange(prevYear);
            onMonthChange(12);
            setYearInput(prevYear.toString());
            setMonthInput('12');
        } else {
            const prevMonth = month - 1;
            onMonthChange(prevMonth);
            setMonthInput(prevMonth.toString().padStart(2, '0'));
        }
    };
    
    const handleNextMonth = () => {
        if (!canGoToNextMonth()) return;
        
        if (month === 12) {
            // Go to January of next year
            const nextYear = year + 1;
            onYearChange(nextYear);
            onMonthChange(1);
            setYearInput(nextYear.toString());
            setMonthInput('01');
        } else {
            const nextMonth = month + 1;
            // Check if next month is in the future
            if (year === maxYear && nextMonth > currentMonth) {
                return;
            }
            onMonthChange(nextMonth);
            setMonthInput(nextMonth.toString().padStart(2, '0'));
        }
    };
    
    // Handle direct year input
    const handleYearKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            const inputYear = parseInt(yearInput);
            if (isNaN(inputYear)) {
                toast({
                    title: t('filters.errors.invalidYear'),
                    variant: 'destructive'
                });
                setYearInput(year.toString());
                return;
            }
            
            if (inputYear < minYear) {
                toast({
                    title: t('filters.errors.yearBeforeData'),
                    description: t('filters.errors.earliestYear', { year: minYear }),
                    variant: 'destructive'
                });
                setYearInput(year.toString());
                return;
            }
            
            if (inputYear > maxYear) {
                toast({
                    title: t('filters.errors.yearInFuture'),
                    variant: 'destructive'
                });
                setYearInput(year.toString());
                return;
            }
            
            onYearChange(inputYear);
            // Adjust month if needed
            if (inputYear === maxYear && month > currentMonth) {
                onMonthChange(currentMonth);
                setMonthInput(currentMonth.toString().padStart(2, '0'));
            }
        }
    };
    
    // Handle direct month input
    const handleMonthKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            const inputMonth = parseInt(monthInput);
            if (isNaN(inputMonth) || inputMonth < 1 || inputMonth > 12) {
                toast({
                    title: t('filters.errors.invalidMonth'),
                    variant: 'destructive'
                });
                setMonthInput(month.toString().padStart(2, '0'));
                return;
            }
            
            if (year === maxYear && inputMonth > currentMonth) {
                toast({
                    title: t('filters.errors.monthInFuture'),
                    variant: 'destructive'
                });
                setMonthInput(month.toString().padStart(2, '0'));
                return;
            }
            
            onMonthChange(inputMonth);
            setMonthInput(inputMonth.toString().padStart(2, '0'));
        }
    };

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Field>
                    <Label>Jahr / Monat</Label>
                    <div className="mt-3 flex items-center rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm">
                        <Button
                            plain
                            onClick={handlePreviousMonth}
                            disabled={!canGoToPreviousMonth()}
                            className="px-2 py-2 rounded-l-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                            aria-label="Vorheriger Monat"
                        >
                            <ChevronLeftIcon className="h-4 w-4" />
                        </Button>
                        
                        <div className="flex-1 flex items-center justify-center gap-1 px-2">
                            <input
                                type="text"
                                value={yearInput}
                                onChange={(e) => setYearInput(e.target.value)}
                                onKeyDown={handleYearKeyPress}
                                className="w-14 text-center bg-transparent outline-none"
                                placeholder="YYYY"
                            />
                            
                            <span className="text-gray-500">/</span>
                            
                            <input
                                type="text"
                                value={monthInput}
                                onChange={(e) => setMonthInput(e.target.value)}
                                onKeyDown={handleMonthKeyPress}
                                className="w-10 text-center bg-transparent outline-none"
                                placeholder="MM"
                            />
                        </div>
                        
                        <Button
                            plain
                            onClick={handleNextMonth}
                            disabled={!canGoToNextMonth()}
                            className="px-2 py-2 rounded-r-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                            aria-label="Nächster Monat"
                        >
                            <ChevronRightIcon className="h-4 w-4" />
                        </Button>
                    </div>
                </Field>

                <Field>
                    <Label>Typ</Label>
                    <Select 
                        value={type} 
                        onChange={(e) => onTypeChange(e.target.value as LedgerEntryType | 'ALL')}
                    >
                        <option value="ALL">Alle</option>
                        <option value="INCOME">Einnahmen</option>
                        <option value="EXPENSE">Ausgaben</option>
                    </Select>
                </Field>

                <Field className="lg:col-span-2">
                    <Label>Suche</Label>
                    <InputGroup>
                        <MagnifyingGlassIcon />
                        <Input
                            type="text"
                            placeholder="Beschreibung durchsuchen..."
                            value={search}
                            onChange={(e) => onSearchChange(e.target.value)}
                        />
                    </InputGroup>
                </Field>
            </div>

            {search && (
                <Field className="flex items-center gap-2">
                    <Checkbox
                        checked={searchAll}
                        onChange={onSearchAllChange}
                    />
                    <Label>In allen Monaten suchen</Label>
                </Field>
            )}
        </div>
    );
}